import type { QueryClient } from "@tanstack/react-query";

import {
  invalidateDashboardAffectedQueries,
  isDashboardAffectedQueryKey,
} from "@/lib/query/dashboard-affected-queries";

/**
 * Request-path fragments for the resources an event-record write can change
 * beyond the dashboard queries, which `dashboard-affected-queries` always adds.
 *
 * `/event-records` matches both the nursery-scoped list
 * (`/v1/nurseries/{id}/event-records`) and a single record
 * (`/v1/event-records/{id}`), which is deliberate: acknowledging or resolving
 * changes the record's detail view, the Events Hub page for EVERY tab, and the
 * dashboard's Events tab.
 *
 * Matching a fragment rather than a whole key is what makes the other tabs
 * refresh. Each Hub tab is its own query key (the tab is a request param), so a
 * mutation site cannot reconstruct them — and the app's global `staleTime` is 5
 * minutes, so a tab fetched moments ago would otherwise be served from cache
 * showing the record in the status it held before the action.
 */
const EVENT_RECORD_AFFECTED_KEY_PATHS = ["/event-records"] as const;

/**
 * True when a React Query key belongs to a resource an event-record write can
 * change. Exported for the drift-guard test.
 */
export function isEventRecordAffectedQueryKey(
  queryKey: readonly unknown[],
): boolean {
  return isDashboardAffectedQueryKey(queryKey, EVENT_RECORD_AFFECTED_KEY_PATHS);
}

/**
 * Invalidate every query that renders data an event-record write can change.
 */
export async function invalidateEventRecordQueries(
  queryClient: QueryClient,
): Promise<void> {
  await invalidateDashboardAffectedQueries(queryClient, {
    keyPaths: EVENT_RECORD_AFFECTED_KEY_PATHS,
  });
}
